import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../../../../shared/components/Card";
import Button from "../../../../../shared/components/Button";
import SecondaryIcon from "../../../../../shared/components/SecondaryIcon";
import Icons from "../../../../../shared/constants/icon.constant";
import { useBankingDrawerRoute } from "../hooks/useBankingDrawerRoute";
import { useDepositDrawerRoute } from "../hooks/useDepositDrawerRoute";
import AvailableBalance from "./AvailableBalance";
import { useFiatBalances } from "@/features/admin/services/fiat";
import ComingSoon from "@/shared/components/ComingSoon";

export default function BankingBalanceCard() {
  const navigate = useNavigate();
  const { showDetailFunds } = useBankingDrawerRoute();
  const { showFiatBankDepositDrawer } = useDepositDrawerRoute();
  const { data: balances = [], isLoading, isError } = useFiatBalances();
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);

  const balanceData = useMemo(() => {
    const baseCurrency = balances[0]?.name?.toUpperCase() ?? "USD";
    if (isError) {
      return {
        amountDisplay: "0.00",
        currencyLabel: baseCurrency,
        errorMessage: "Failed to load balance",
      };
    }

    // Calculate total balance using provided rate (assumed base currency).
    const totalInUSD = balances.reduce((total, balance) => {
      const amount = parseFloat(balance.amount) || 0;
      const rate = balance.rate || 1;
      return total + amount * rate;
    }, 0);

    return {
      amountDisplay: totalInUSD.toFixed(2),
      currencyLabel: baseCurrency,
      errorMessage: undefined,
    };
  }, [balances, isError]);

  return (
    <Card title="Banking" titleSize="md">
      <div className="w-full flex flex-col gap-6 py-4">
        <div className="flex gap-3 w-full justify-between">
          <div className="my-5">
            <AvailableBalance
              amountDisplay={balanceData.amountDisplay}
              currencyLabel={balanceData.currencyLabel}
              errorMessage={balanceData.errorMessage}
              isLoading={isLoading}
            />
          </div>

          {/* Deposit and Withdraw Buttons */}
          <div className="flex flex-col gap-4">
            <Button
              variant="secondary"
              className="bg-main-dark/50! rounded-lg! gap-2 justify-between! py-3 text-label-dark!"
              fluid
              onClick={() => showFiatBankDepositDrawer()}>
              <SecondaryIcon icon={Icons.Deposit({ width: 16, height: 16 })} />
              Top Up
            </Button>
            <Button
              variant="secondary"
              className="bg-main-dark/50! rounded-lg! gap-2 justify-between! py-3 text-label-dark!"
              fluid
              onClick={() => navigate("/assets/withdrawal")}>
              <SecondaryIcon
                icon={Icons.Withdrawal({ width: 16, height: 16 })}
              />
              Withdraw
            </Button>
          </div>
        </div>

        <div className="flex flex-col w-full gap-2">
          <p className="text-sm">Funds</p>
          {balances.map((balance) => (
            <button
              key={balance.id}
              className="hover:cursor-pointer"
              onClick={showDetailFunds}>
              <div className="bg-main-dark/30 p-4 rounded-lg flex flex-col gap-4">
                <img
                  src={`/icon-${balance.name.toLowerCase()}.svg`}
                  alt={balance.name}
                  className="rounded-full h-10 w-10"
                  onError={(e) => {
                    e.currentTarget.src = "/icon-usd.svg";
                  }}
                />
                <div className="flex justify-between">
                  <div className="flex flex-col items-start">
                    <p className="font-semibold">
                      {balance.name.toUpperCase()}
                    </p>
                    <p className="text-sm text-label-dark">{balance.status}</p>
                  </div>
                  <div className="flex flex-col items-end">
                    <p className="font-semibold">
                      {parseFloat(balance.amount).toFixed(2)}{" "}
                      {balance.name.toUpperCase()}
                    </p>
                    <p className="text-sm text-label-dark">Balance</p>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Add Card Section */}
        {/* <div className="flex flex-col w-full gap-2">
          <p className="text-sm">Cards</p>
          <button
            onClick={() => setIsComingSoonOpen(true)}
            className="bg-main-dark/30 p-6 rounded-lg border-2 border-dashed border-white/20 hover:border-white/40 hover:bg-main-dark/50 transition-all flex flex-col items-center justify-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Plus className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm text-white/70">Add New Card</p>
          </button>
        </div> */}
      </div>

      {/* Coming Soon Modal */}
      {isComingSoonOpen && (
        <ComingSoon onClose={() => setIsComingSoonOpen(false)} />
      )}
    </Card>
  );
}
